// src/pages/AgreementDeclinedPage.js
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAgreementByAgreementID, saveAgreementToUser } from '../firebase/services';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import CircleIcon, { SvgIcon } from '../components/Icon';
import Lightbox from '../components/Lightbox';

export default function AgreementDeclinedPage() {
  const { agreementID } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    getAgreementByAgreementID(agreementID).then(a => { setAgreement(a); setLoading(false); });
  }, [agreementID]);

  const handleSave = async () => {
    if (!user) return navigate('/login');
    await saveAgreementToUser(user.uid, agreementID);
    setSaved(true);
  };

  if (loading) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('declinedPage.loading')}</div></div>;
  if (!agreement) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('declinedPage.notFound')}</div></div>;

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>

        <div className="hero hero-declined">
          <div className="hero-icon" style={{ display: 'flex', justifyContent: 'center' }}><CircleIcon name="x" color="rgba(255,255,255,0.25)" size={52} iconSize={26} /></div>
          <div className="hero-title">{t('declinedPage.title')}</div>
          <div className="hero-sub">{t('declinedPage.subtitle')}</div>
        </div>

        <div className="card">
          <div className="detail-row">
            <div className="detail-label">{t('declinedPage.agreementTitle')}</div>
            <div className="detail-value" style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700 }}>{agreement.title}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">{t('declinedPage.details')}</div>
            <div className="detail-value" style={{ color: 'var(--muted)', fontSize: '0.86rem' }}>{agreement.details}</div>
          </div>
          {agreement.deadline && (
            <div className="detail-row">
              <div className="detail-label">{t('declinedPage.deadline')}</div>
              <div className="detail-value">{new Date(agreement.deadline).toLocaleString()}</div>
            </div>
          )}
          {agreement.appointmentDate && (
            <div className="detail-row">
              <div className="detail-label">{t('declinedPage.appointmentDate')}</div>
              <div className="detail-value">{new Date(agreement.appointmentDate).toLocaleString()}</div>
            </div>
          )}
          {agreement.evidence?.length > 0 && (
            <div className="detail-row">
              <div className="detail-label"><SvgIcon name="paperclip" size={13} color="var(--text)" /> {t('declinedPage.evidence', { count: agreement.evidence.length })}</div>
              <div className="evidence-view-grid" style={{ marginTop: 6 }}>
                {agreement.evidence.map((ev, i) =>
                  ev.type?.includes('pdf') ? (
                    <a key={i} href={ev.url} target="_blank" rel="noreferrer" className="evidence-view-thumb">
                      <SvgIcon name="doc" size={18} color="var(--text)" />
                    </a>
                  ) : (
                    <div key={i} className="evidence-view-thumb" style={{ cursor: 'pointer' }} onClick={() => setLightboxSrc(ev.url)}>
                      <img src={ev.url} alt="evidence" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    </div>
                  )
                )}
              </div>
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: '0.72rem', color: 'var(--muted)' }}>
            {t('declinedPage.agreementID')} <strong>{agreementID}</strong>
          </div>
        </div>

        <div className="status-inactive">{t('declinedPage.noLongerActive')}</div>

        {!saved ? (
          user ? (
            <button className="btn btn-secondary" onClick={handleSave}>
              <SvgIcon name="save" size={14} color="var(--text)" /> {t('declinedPage.saveAgreement')}
            </button>
          ) : (
            <button className="btn btn-secondary" onClick={() => navigate('/login')}>
              {t('declinedPage.savePrompt')}
            </button>
          )
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--confirmed)', fontWeight: 600, marginBottom: 10, fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <SvgIcon name="check" size={14} color="var(--confirmed)" /> {t('declinedPage.saved')}
          </div>
        )}

        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          {t('declinedPage.backToDashboard')}
        </button>
      </div>
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
